import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Target, Calendar as CalendarIcon, Plus, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { Slider } from "@/components/ui/slider";

interface AddGrowthGoalModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit?: (goalData: {
    title: string;
    category: string;
    dueDate: Date;
    description: string;
    progress: number;
    priority: 'low' | 'medium' | 'high';
    skills_needed: string[];
  }) => void;
}

export default function AddGrowthGoalModal({
  isOpen,
  onOpenChange,
  onSubmit,
}: AddGrowthGoalModalProps) {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [dueDate, setDueDate] = useState<Date>();
  const [description, setDescription] = useState("");
  const [progress, setProgress] = useState(0);
  const [priority, setPriority] = useState<'low' | 'medium' | 'high'>('medium');
  const [skillsNeeded, setSkillsNeeded] = useState<string[]>([]);
  const [newSkill, setNewSkill] = useState("");
  const { toast } = useToast();

  const SKILL_SUGGESTIONS = [
    "React", "TypeScript", "Python", "Leadership", "Communication",
    "Project Management", "Data Analysis", "UI/UX Design", "DevOps",
    "Machine Learning", "Public Speaking", "Mentoring", "Strategic Thinking"
  ];

  const handleSubmit = () => {
    if (!title || !category || !dueDate) {
      toast({
        title: "Missing information",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    onSubmit?.({
      title,
      category,
      dueDate,
      description,
      progress,
      priority,
      skills_needed: skillsNeeded,
    });

    // Reset form
    setTitle("");
    setCategory("");
    setDueDate(undefined);
    setDescription("");
    setProgress(0);
    setPriority('medium');
    setSkillsNeeded([]);
    setNewSkill("");
    onOpenChange(false);
  };

  const addSkill = (skill: string) => {
    if (skill.trim() && !skillsNeeded.includes(skill.trim())) {
      setSkillsNeeded([...skillsNeeded, skill.trim()]);
      setNewSkill("");
    }
  };

  const removeSkill = (skill: string) => {
    setSkillsNeeded(skillsNeeded.filter(s => s !== skill));
  };

  const filteredSuggestions = SKILL_SUGGESTIONS.filter(
    skill => 
      !skillsNeeded.includes(skill) &&
      skill.toLowerCase().includes(newSkill.toLowerCase())
  );

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-primary" />
            <span>Add New Growth Goal</span>
          </DialogTitle>
          <DialogDescription>
            Set a new goal to track your personal and professional growth.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="goal-title">Goal Title</Label>
            <Input
              id="goal-title"
              placeholder="What do you want to achieve?"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="goal-category">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger id="goal-category">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Technical">Technical</SelectItem>
                  <SelectItem value="Leadership">Leadership</SelectItem>
                  <SelectItem value="Communication">Communication</SelectItem>
                  <SelectItem value="Personal">Personal</SelectItem>
                  <SelectItem value="Professional">Professional</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="goal-priority">Priority</Label>
              <Select value={priority} onValueChange={(value: 'low' | 'medium' | 'high') => setPriority(value)}>
                <SelectTrigger id="goal-priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="goal-description">Description</Label>
            <Textarea
              id="goal-description"
              placeholder="Describe your goal and what success looks like..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="min-h-[100px]"
            />
          </div>

          <div className="grid gap-2">
            <Label>Initial Progress</Label>
            <div className="space-y-2">
              <Slider
                value={[progress]}
                onValueChange={(value) => setProgress(value[0])}
                max={100}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>0%</span>
                <span>{progress}%</span>
                <span>100%</span>
              </div>
            </div>
          </div>

          {/* Skills Needed */}
          <div className="grid gap-2">
            <Label>Skills Needed (optional)</Label>
            <p className="text-xs text-muted-foreground">
              Add skills that this goal will help you develop
            </p>
            
            <div className="flex gap-2">
              <Input
                placeholder="Add a skill..."
                value={newSkill}
                onChange={(e) => setNewSkill(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addSkill(newSkill);
                  }
                }}
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => addSkill(newSkill)}
                disabled={!newSkill.trim()}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            {/* Skill Suggestions */}
            {newSkill && filteredSuggestions.length > 0 && (
              <div className="flex flex-wrap gap-1 p-2 border rounded-lg bg-muted/50">
                {filteredSuggestions.slice(0, 5).map((skill) => (
                  <Badge
                    key={skill}
                    variant="outline"
                    className="cursor-pointer hover:bg-primary hover:text-primary-foreground text-xs"
                    onClick={() => addSkill(skill)}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    {skill}
                  </Badge>
                ))}
              </div>
            )}

            {/* Selected Skills */}
            {skillsNeeded.length > 0 && (
              <div className="flex flex-wrap gap-1 p-2 border rounded-lg">
                {skillsNeeded.map((skill) => (
                  <Badge key={skill} variant="secondary">
                    {skill}
                    <button
                      type="button"
                      onClick={() => removeSkill(skill)}
                      className="ml-1 hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <div className="grid gap-2">
            <Label>Due Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !dueDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dueDate ? format(dueDate, "PPP") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={dueDate}
                  onSelect={setDueDate}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        <DialogFooter>
          <Button type="submit" onClick={handleSubmit}>
            Add Goal
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
